/**
 * Frontend contract for suspected duplicate claimant pairs (Story #6717,
 * backend Story #6518).
 *
 * Mirrors the Go DTO `duplicatePairResponse` in coronis-apps
 * (`apps/mgmt-api/cmd/handlers/claimant_duplicate_pairs.go`), served from
 * `GET /duplicate-claimant-pairs`.
 *
 * THIS module is the single isolation point for the backend contract — the DTO
 * shape and the status/severity unions all live here, so a backend change
 * touches one file. Same pattern as `deleteImpact.ts` (#5751 / ADR-078).
 *
 * MONEY ARRIVES AS STRINGS, deliberately. These amounts are compared against a
 * specific deductible and JSON floats cannot represent them exactly. Do not
 * parseFloat them for anything but display.
 */

/**
 * Which producer found the pair. `manual` is an operator selecting two claimants
 * in the Claimants list (#7591) -- the duplicates no detector can reach.
 */
export type DuplicateDetectionSource = "load_time_guard" | "sweep" | "manual";

/**
 * Which rule fired. Load-time reasons come from a matcher guard rejecting a
 * name-tier candidate; sweep reasons come from the scheduled variant sweep
 * (#6719), which finds pairs the matcher never compared.
 */
export type DuplicateDetectionReason =
  | "dob_mismatch"
  | "member_id_divergent"
  | "same_name_siblings"
  | "subscriber_id_divergent"
  | "relationship_divergent"
  | "sweep_first_name_dob"
  | "sweep_last_name_dob"
  | "manual";

/**
 * Which vendor's numbering a member id came from. The cross-vendor split class
 * exists because a medical TPA and a PBM issue different ids for one person
 * (#6499), so the reviewer needs to see which space each side sits in to make
 * sense of "the ids differ".
 */
export type DuplicateIdentifierSpace = "medical" | "rx";

export type DuplicatePairSeverity = "high" | "medium" | "low";

export type DuplicatePairStatus = "open" | "confirmed" | "dismissed_not_duplicate" | "merged" | "self_resolved";

export interface DuplicateClaimantPair {
  id: string;
  client_id: string;
  policy_id?: string;

  /**
   * Ordered pair. `claimant_a_id < claimant_b_id` is a storage invariant so
   * (A,B) and (B,A) are one row — it carries NO survivor semantics. Which row
   * survives a merge is decided at merge time (#6519), not by this ordering.
   */
  claimant_a_id: string;
  claimant_b_id: string;

  detection_source: DuplicateDetectionSource;
  detection_reason: DuplicateDetectionReason;

  matched_on?: string;
  differing_fields: string[];
  claimant_a_identifier_space?: DuplicateIdentifierSpace;
  claimant_b_identifier_space?: DuplicateIdentifierSpace;

  /** Decimal strings — see the module note. */
  claimant_a_paid_amount: string;
  claimant_b_paid_amount: string;
  combined_paid_amount: string;
  specific_deductible?: string;
  /** Combined spend / specific deductible. Absent when no deductible resolved. */
  severity_ratio?: string;

  severity: DuplicatePairSeverity;
  source_document_id?: string;
  status: DuplicatePairStatus;

  dismissal_reason?: string;
  dismissed_by?: string;
  dismissed_at?: string;
  resolved_at?: string;

  /**
   * True when the pair was dismissed earlier and has returned because the
   * underlying evidence changed. The list MUST distinguish these: a reviewer who
   * cannot see that the evidence moved will re-dismiss on reflex, which defeats
   * the whole point of the fingerprint mechanism (#6518, the #6496 lesson).
   */
  was_re_raised: boolean;
  created_at: string;
}

/** Human labels for the Status column (#7304). */
export const DUPLICATE_PAIR_STATUS_LABELS: Record<DuplicatePairStatus, string> = {
  open: "Open",
  confirmed: "Confirmed",
  dismissed_not_duplicate: "Dismissed",
  merged: "Merged",
  self_resolved: "Self-resolved",
};

/** Human labels for the "how was this found" column. */
export const DETECTION_REASON_LABELS: Record<DuplicateDetectionReason, string> = {
  dob_mismatch: "Date of birth differs",
  member_id_divergent: "Member ID differs",
  same_name_siblings: "Same name, multiple matches",
  subscriber_id_divergent: "Subscriber ID differs",
  relationship_divergent: "Relationship differs",
  sweep_first_name_dob: "First name + DOB match",
  sweep_last_name_dob: "Last name + DOB match",
  manual: "Manual selection",
};

/** Human labels for the "Found By" column. */
export const DETECTION_SOURCE_LABELS: Record<DuplicateDetectionSource, string> = {
  load_time_guard: "At load",
  sweep: "Sweep",
  manual: "Manual",
};

/** Human labels for the differing-field chips. */
export const DIFFERING_FIELD_LABELS: Record<string, string> = {
  claimant_birth_date: "DOB",
  member_id: "Member ID",
  claimant_name: "Name",
  claimant_first_name: "First name",
  claimant_last_name: "Last name",
  rx_member_id: "Rx Member ID",
  claimant_related_subscriber_id: "Subscriber ID",
  claimant_relationship: "Relationship",
  same_name_ambiguity: "Ambiguous name match",
};
